import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../supabase';
import { DEFAULT_ROLE_PERMISSIONS, type RolePermissionMap } from '../../utils/rolePermissions';
import { backfillNewMenus } from './backfill';
import { canAccessMenu as canAccessMenuImpl, canUseFeature as canUseFeatureImpl } from './canAccess';

export function useRolePermissionsState() {
  const [rolePermissions, setRolePermissions] = useState<RolePermissionMap>({} as RolePermissionMap);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load permissions from database on mount.
  // `silent` = refresh background (interval/event) TIDAK menyalakan `loading` global,
  // supaya ProtectedRoute tidak meng-unmount halaman (penyebab kedip/refetch tiap 5 menit).
  const loadPermissions = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    // Skip loading on public routes (form viewer, landing pages)
    const path = window.location.pathname;
    if (path.startsWith('/f/') || path.startsWith('/lp/') || path.startsWith('/d/') || path.startsWith('/ai-lp/')) {
      setLoading(false);
      setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
      return;
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      const { data, error: dbError } = await supabase
        .from('settings')
        .select('role_permissions')
        .eq('id', 'rolePermissions')
        .maybeSingle();

      if (dbError) {
        throw dbError;
      }

      if (data?.role_permissions) {
        // Use database permissions directly - what's in the UI checklist is what gets used
        // Database completely overrides defaults
        console.log('💾 Loaded permissions from database:', Object.keys(data.role_permissions));
        setRolePermissions(backfillNewMenus(data.role_permissions as RolePermissionMap));
      } else {
        console.log('⚠️ No custom permissions in DB, initializing with defaults for first-time setup');
        setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
      }
    } catch (err) {
      console.error('❌ Error loading role permissions:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      // On error, use defaults to avoid blocking the app
      console.log('⚠️ Fallback to defaults due to error');
      setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Load on mount (non-silent: boleh tampilkan spinner sekali saja)
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Listen for manual refresh events from permission manager (silent — jangan kedip)
  useEffect(() => {
    const handleRefresh = () => {
      loadPermissions({ silent: true });
    };
    window.addEventListener('rolePermissionsUpdated', handleRefresh);
    return () => window.removeEventListener('rolePermissionsUpdated', handleRefresh);
  }, [loadPermissions]);

  // Refresh permissions periodically — SILENT (tanpa spinner/unmount halaman)
  useEffect(() => {
    const interval = setInterval(() => loadPermissions({ silent: true }), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadPermissions]);

  const canAccessMenu = useCallback(
    (menuId: string, userRole: string) => canAccessMenuImpl(rolePermissions, menuId, userRole),
    [rolePermissions]
  );

  const canUseFeature = useCallback(
    (featureId: string, userRole?: string) => canUseFeatureImpl(rolePermissions, featureId, userRole),
    [rolePermissions]
  );

  const refreshPermissions = useCallback(async () => {
    await loadPermissions();
  }, [loadPermissions]);

  return { rolePermissions, loading, error, canAccessMenu, canUseFeature, refreshPermissions };
}
